/**
 * FRONT-DESK-POSSIBLE-MATCH-SURFACE-01 — pure helpers for the lead ->
 * patient crossmatch advisory surface.
 *
 * Pure by construction — no React, no fetch, no Date — so every mapper and
 * the flag-gate decision unit-test without jsdom (the
 * patient-master-view.ts / leads-workspace-view.ts doctrine).
 *
 * SECURITY / HONESTY POSTURE:
 *   - The enable decision reads ONLY the SERVER-computed
 *     `me.patientMasterEnabled` signal (absent = disabled, fail-closed) —
 *     the SAME signal the Patient Master surface uses, so this REUSES the
 *     existing PATIENT_MASTER_V1_ENABLED flag and invents none. Backend
 *     route auth remains the security boundary. With the flag off the
 *     surface neither renders NOR fetches.
 *   - The wire contract mirrors api/routes/patient-master-lead-crossmatch.ts
 *     (separate-packages convention, kept in lockstep by review): an opaque
 *     verdict + a non-PHI count ONLY. No phone / name / dob / email /
 *     fingerprint, and no patient UUID — the surface is display-only and
 *     never links, reveals, merges, or creates.
 *   - 'not_available' (and any fetch error) render NOTHING — never a false
 *     "No existing patient match".
 */

import type { DashboardMe } from '@/dashboard/types';

/** Wire-contract mirror of the backend CrossmatchVerdict. */
export type CrossmatchVerdict =
  | 'no_match'
  | 'possible_match'
  | 'strong_match'
  | 'not_available';

/** Wire-contract mirror of the backend LeadPatientCrossmatchResult. */
export interface LeadPatientCrossmatchResult {
  readonly verdict: CrossmatchVerdict;
  /** In-scope patients sharing the lead's phone. Non-PHI count, no identity. */
  readonly candidateCount: number;
}

/**
 * Whether the crossmatch surface may render AND fetch. Keyed on the
 * server-computed `me.patientMasterEnabled` (present only when
 * PATIENT_MASTER_V1_ENABLED is on AND the role is in the audience).
 * ABSENT = disabled (fail-closed). The self-fetching notice guards its
 * effect on this, so with the flag off NO request is ever issued.
 */
export function isLeadCrossmatchEnabled(me: DashboardMe | null): boolean {
  return me?.patientMasterEnabled === true;
}

/**
 * Status-chip tone per verdict (reuses the existing .status-chip--*
 * tokens — no new design doctrine). `not_available` never reaches the UI
 * (it renders nothing) but is mapped for exhaustiveness.
 */
const VERDICT_CHIP: Readonly<Record<CrossmatchVerdict, string>> = {
  strong_match: 'status-chip status-chip--partial',
  possible_match: 'status-chip status-chip--partial',
  no_match: 'status-chip status-chip--neutral',
  not_available: 'status-chip status-chip--off',
};

/**
 * The HONEST copy per verdict (exact strings — asserted in tests):
 *   possible_match -> "Possible existing patient — needs review"
 *   strong_match   -> "Likely existing patient — confirm"
 *   no_match       -> "No existing patient match"
 *   not_available  -> null (render NOTHING — never a false "no patient")
 */
export const CROSSMATCH_COPY: Readonly<
  Record<CrossmatchVerdict, string | null>
> = {
  possible_match: 'Possible existing patient — needs review',
  strong_match: 'Likely existing patient — confirm',
  no_match: 'No existing patient match',
  not_available: null,
};

export interface LeadCrossmatchDisplay {
  readonly verdict: CrossmatchVerdict;
  readonly text: string;
  readonly chipClass: string;
}

/**
 * Pure verdict -> display mapper. Returns null when nothing should render:
 *   - `result` is null (not fetched / disabled), OR
 *   - the verdict is 'not_available' (we could not compute — render
 *     NOTHING, never a false negative).
 * Every other verdict returns its EXACT honest copy + a tone chip. Note
 * `candidateCount` is intentionally NOT rendered: the advisory copy is the
 * whole message, and a count adds no value while widening the surface.
 */
export function describeLeadCrossmatch(
  result: LeadPatientCrossmatchResult | null,
): LeadCrossmatchDisplay | null {
  if (result === null) return null;
  const text = CROSSMATCH_COPY[result.verdict];
  if (text === null) return null; // not_available -> render nothing
  return {
    verdict: result.verdict,
    text,
    chipClass: VERDICT_CHIP[result.verdict],
  };
}
